import { REGISTRY_CURRENCIES, normalizeRegistryCurrency } from "./currencyRegistry.js";
import {
  PLATFORM_FALLBACK_LOCALE,
  REGISTRY_LOCALES,
  normalizeRegistryLocale,
} from "./localeRegistry.js";
import { DEFAULT_MARKET_CODE, getMarketProfile, normalizeManifestMarket } from "./marketProfiles.js";

export const MANIFEST_I18N_LOCALES = REGISTRY_LOCALES;
export const MANIFEST_I18N_CURRENCIES = REGISTRY_CURRENCIES;

export const DEFAULT_MANIFEST_I18N = {
  defaultLocale: "es-CO",
  supportedLocales: ["es-CO"],
  defaultCurrency: "COP",
  supportedCurrencies: ["COP"],
  fallbackLocale: "es-CO",
};

export function normalizeManifestLocale(value) {
  const normalized = normalizeRegistryLocale(value);
  return MANIFEST_I18N_LOCALES.includes(normalized) ? normalized : DEFAULT_MANIFEST_I18N.defaultLocale;
}

export function normalizeManifestCurrency(value) {
  const normalized = normalizeRegistryCurrency(value);
  return MANIFEST_I18N_CURRENCIES.includes(normalized) ? normalized : DEFAULT_MANIFEST_I18N.defaultCurrency;
}

function uniqueList(values, fallback) {
  const seen = new Set();
  const result = [];
  for (const value of values || []) {
    const normalized = typeof value === "string" ? value.trim() : "";
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result.length ? result : [...fallback];
}

export function normalizeManifestI18n(raw, market = null) {
  const profile = market ? getMarketProfile(market.code || DEFAULT_MARKET_CODE) : null;
  const profileDefaults = profile
    ? {
        defaultLocale: profile.defaultLocale,
        supportedLocales: [...profile.supportedLocales],
        defaultCurrency: profile.defaultCurrency,
        supportedCurrencies: [...profile.supportedCurrencies],
        fallbackLocale: profile.defaultLocale,
      }
    : { ...DEFAULT_MANIFEST_I18N };

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return profile ? { ...profileDefaults } : { ...DEFAULT_MANIFEST_I18N };
  }

  const defaultLocale = normalizeManifestLocale(raw.defaultLocale || profileDefaults.defaultLocale);
  const supportedLocales = uniqueList(
    (raw.supportedLocales || profileDefaults.supportedLocales)
      .map(normalizeManifestLocale)
      .filter((locale) => MANIFEST_I18N_LOCALES.includes(locale)),
    [defaultLocale]
  );

  if (!supportedLocales.includes(defaultLocale)) {
    supportedLocales.unshift(defaultLocale);
  }

  const defaultCurrency = normalizeManifestCurrency(raw.defaultCurrency || profileDefaults.defaultCurrency);
  const supportedCurrencies = uniqueList(
    (raw.supportedCurrencies || profileDefaults.supportedCurrencies)
      .map(normalizeManifestCurrency)
      .filter((currency) => MANIFEST_I18N_CURRENCIES.includes(currency)),
    [defaultCurrency]
  );

  if (!supportedCurrencies.includes(defaultCurrency)) {
    supportedCurrencies.unshift(defaultCurrency);
  }

  const fallbackLocale = normalizeManifestLocale(raw.fallbackLocale || defaultLocale);

  return {
    defaultLocale,
    supportedLocales,
    defaultCurrency,
    supportedCurrencies,
    fallbackLocale,
  };
}

export function normalizeManifestWithMarket(manifest) {
  const market = normalizeManifestMarket(manifest?.market);
  const i18n = normalizeManifestI18n(manifest?.i18n, market);
  return { market, i18n };
}

export { normalizeManifestMarket, resolveManifestMarket, getMarketProfileForManifest } from "./marketProfiles.js";
